/**
 * CR425 + CR450: ValueBoxBlock (V2) — Deterministic, deal-type-aware
 *
 * Uses getValueBoxDisplay() SSOT resolver for all deal types.
 * CR450: Reads display mode config from context.components.value_box
 *   primaryMetric, secondaryMetric, rounding, showApproxSign, showIcon
 *
 * POINTS deals still delegate to PointsValueBoxBlock for specialized rendering.
 */

import type { FC } from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { getValueBoxDisplay } from "@/lib/deal-cards/displayValueResolver";
import { formatCurrency, formatPercent } from "@/lib/deal-cards/bonusValueModel";
import { PointsValueBoxBlock } from "./PointsValueBoxBlock";
import { TrendingUp, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// CR450: Config shape from components_json.components.value_box
interface ValueBoxDisplayConfig {
  primaryMetric?: "currency" | "percent";
  secondaryMetric?: "none" | "currency" | "percent" | "auto";
  primaryRounding?: number;
  secondaryRounding?: number;
  /** @deprecated legacy key */
  valueFormat?: "percent" | "currency";
  rounding?: number;
  showIcon?: boolean;
  showApproxSign?: boolean;
}

// Deal-type color map
const COLOR_MAP: Record<string, { bg: string; border: string; icon: string; text: string; textBold: string }> = {
  BONUS:    { bg: "bg-primary/10", border: "border-primary/20", icon: "text-primary", text: "text-primary", textBold: "text-primary" },
  DISCOUNT: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", text: "text-orange-900", textBold: "text-orange-700" },
  IN_KIND:  { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", text: "text-amber-900", textBold: "text-amber-700" },
  POINTS:   { bg: "bg-purple-50/50", border: "border-purple-200/50", icon: "text-purple-500", text: "text-purple-700", textBold: "text-purple-800" },
  DEFAULT:  { bg: "bg-primary/10", border: "border-primary/20", icon: "text-primary", text: "text-primary", textBold: "text-primary" },
};

// CR450: Format with configurable rounding
function fmtCurrency(val: number, decimals = 0): string {
  if (decimals === 0) return val >= 10 ? `$${Math.round(val).toLocaleString()}` : `$${val.toFixed(2)}`;
  return `$${val.toFixed(decimals)}`;
}

function fmtPercent(val: number, decimals = 0): string {
  return decimals === 0 ? `${Math.round(val)}%` : `${val.toFixed(decimals)}%`;
}

export const ValueBoxBlock: FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  if (!block.enabled) return null;

  const { category } = getDealType(deal);

  // POINTS deals use specialized display
  if (category === "POINTS") {
    return <PointsValueBoxBlock deal={deal} block={block} context={context} />;
  }

  const display = getValueBoxDisplay(deal);
  const colors = COLOR_MAP[category] ?? COLOR_MAP.DEFAULT;

  // CR450: Read config from components_json
  const cfg = (context.components?.value_box ?? {}) as ValueBoxDisplayConfig;
  const primaryMetric = cfg.primaryMetric ?? cfg.valueFormat ?? (display.mode === "empty" ? "currency" : display.mode);
  const secondaryMetric = cfg.secondaryMetric ?? "auto";
  const primaryRounding = cfg.primaryRounding ?? cfg.rounding ?? 0;
  const secondaryRounding = cfg.secondaryRounding ?? 0;
  const showIcon = cfg.showIcon !== false;
  const showApprox = cfg.showApproxSign !== false;

  // CR450: Canonical label key with legacy fallback
  const label = context.labels?.value_box_title
    || context.labels?.value_title
    || context.labels?.effective_value_title
    || display.label;

  // --- Empty: admin placeholder or hide ---
  if (display.mode === "empty") {
    if (context.isAdmin) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md border border-dashed border-muted-foreground/30"
                data-block="value_box"
                data-block-id={block.id}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground/50" />
                  <span className="text-sm text-muted-foreground/60 italic">
                    Value data missing
                  </span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              <p className="font-semibold">Why hidden?</p>
              <p className="mt-1">{display.reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  }

  // CR450: Resolve primary and secondary values based on config
  // The resolver gives us: display.mode (its natural pick), display.value, display.secondaryValue
  // We need currency and percent values to allow the config to pick either.
  const currencyVal = display.mode === "currency" ? display.value : display.secondaryValue;
  const percentVal = display.mode === "percent" ? display.value : display.secondaryValue;

  // Primary value
  let primaryText: string | null = null;
  let primarySuffix = "";
  if (primaryMetric === "currency" && currencyVal != null) {
    primaryText = fmtCurrency(currencyVal, primaryRounding);
  } else if (primaryMetric === "percent" && percentVal != null) {
    primaryText = fmtPercent(percentVal, primaryRounding);
    primarySuffix = category === "DISCOUNT" ? " off" : " back";
  } else if (primaryMetric === "currency" && percentVal != null) {
    // Fallback: requested currency but only percent available
    primaryText = fmtPercent(percentVal, primaryRounding);
    primarySuffix = category === "DISCOUNT" ? " off" : " back";
  } else if (primaryMetric === "percent" && currencyVal != null) {
    // Fallback: requested percent but only currency available
    primaryText = fmtCurrency(currencyVal, primaryRounding);
  }

  // Secondary value
  let secondaryText: string | null = null;
  if (secondaryMetric === "none") {
    secondaryText = null;
  } else if (secondaryMetric === "currency" && currencyVal != null && primaryMetric !== "currency") {
    secondaryText = `${showApprox ? "≈ " : ""}${fmtCurrency(currencyVal, secondaryRounding)} value`;
  } else if (secondaryMetric === "percent" && percentVal != null && primaryMetric !== "percent") {
    secondaryText = `${showApprox ? "≈ " : ""}${fmtPercent(percentVal, secondaryRounding)} back`;
  } else if (secondaryMetric === "auto") {
    // Show the "other" metric if available
    if (primaryMetric === "currency" && percentVal != null) {
      secondaryText = `${showApprox ? "≈ " : ""}${fmtPercent(percentVal, secondaryRounding)} back`;
    } else if (primaryMetric === "percent" && currencyVal != null) {
      secondaryText = `${showApprox ? "≈ " : ""}${fmtCurrency(currencyVal, secondaryRounding)} value`;
    }
  }

  // If we couldn't produce any primary text, hide in public / placeholder in admin
  if (!primaryText) {
    if (context.isAdmin) {
      return (
        <div
          className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md border border-dashed border-muted-foreground/30"
          data-block="value_box"
          data-block-id={block.id}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-muted-foreground/50" />
            <span className="text-sm text-muted-foreground/60 italic">
              No {primaryMetric} data for this deal
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={`flex items-center justify-between py-2 px-3 ${colors.bg} rounded-md border ${colors.border}`}
      data-block="value_box"
      data-block-id={block.id}
      data-component-key="value_box"
    >
      <div className="flex items-center gap-2">
        {showIcon && <TrendingUp className={`h-4 w-4 ${colors.icon}`} />}
        <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-lg font-semibold ${colors.textBold}`}>
          {primaryText}{primarySuffix}
        </span>
        {secondaryText && (
          <span className={`text-sm ${colors.textBold} opacity-70`}>
            {secondaryText}
          </span>
        )}
      </div>
    </div>
  );
};

export default ValueBoxBlock;
